/**
 * Kakara — Collection category tabs (collection page)
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'kakara-collection-tab';
  const PARENTS = {
    'car-fragrance': {
      defaultChild: 'car-fragrance-hanger',
      children: [
        'car-fragrance-hanger',
        'car-fragrance-refillable',
        'car-fragrance-refills',
      ],
    },
  };

  function parentOf(tabId) {
    if (!tabId) return null;
    if (PARENTS[tabId]) return tabId;
    return Object.keys(PARENTS).find((parent) => PARENTS[parent].children.includes(tabId)) || null;
  }

  function resolveTarget(tabId, panelMap) {
    if (tabId && PARENTS[tabId]) {
      const child = PARENTS[tabId].defaultChild;
      if (panelMap.has(child)) return child;
    }
    if (tabId && panelMap.has(tabId)) return tabId;
    return null;
  }

  function init() {
    const tablist = document.querySelector('[data-collection-tabs]');
    const panelsRoot = document.querySelector('[data-collection-panels]');
    if (!tablist || !panelsRoot) return;

    const categoryTabs = Array.from(tablist.querySelectorAll('[data-collection-tab]'));
    const subtabNavs = Array.from(panelsRoot.querySelectorAll('[data-collection-subtabs]'));
    const subtabs = Array.from(panelsRoot.querySelectorAll('[data-collection-subtabs] [data-collection-tab]'));
    const panels = Array.from(panelsRoot.querySelectorAll('[data-collection-panel]'));
    if (!categoryTabs.length || !panels.length) return;

    const defaultTab = tablist.dataset.defaultTab || categoryTabs[0]?.dataset.collectionTab;
    const panelMap = new Map(panels.map((panel) => [panel.dataset.collectionPanel, panel]));

    function setActive(tabId, { updateHash = true, store = true } = {}) {
      const target = resolveTarget(tabId, panelMap) || resolveTarget(defaultTab, panelMap);
      if (!target) return;
      const parent = parentOf(target);

      categoryTabs.forEach((tab) => {
        const id = tab.dataset.collectionTab;
        const isActive = id === target || id === parent;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.tabIndex = isActive ? 0 : -1;
      });

      subtabs.forEach((tab) => {
        const isActive = tab.dataset.collectionTab === target;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.tabIndex = isActive ? 0 : -1;
      });

      subtabNavs.forEach((nav) => {
        const show = nav.dataset.parentTab === parent;
        nav.hidden = !show;
      });

      panels.forEach((panel) => {
        const isActive = panel.dataset.collectionPanel === target;
        panel.hidden = !isActive;
        panel.classList.toggle('is-active', isActive);
      });

      const toolbar = panelsRoot.querySelector('[data-collection-toolbar]');
      if (toolbar) {
        toolbar.hidden = target !== defaultTab && parent !== defaultTab;
      }

      if (store) {
        try {
          sessionStorage.setItem(STORAGE_KEY, target);
        } catch (e) {
          /* ignore */
        }
      }

      if (updateHash && target !== defaultTab) {
        const url = new URL(window.location.href);
        url.hash = target;
        history.replaceState(null, '', url.toString());
      } else if (updateHash && window.location.hash) {
        const url = new URL(window.location.href);
        url.hash = '';
        history.replaceState(null, '', url.toString());
      }
    }

    function onTabClick(event) {
      const tab = event.target.closest('[data-collection-tab]');
      if (!tab) return;
      if (!tablist.contains(tab) && !panelsRoot.contains(tab)) return;
      event.preventDefault();
      setActive(tab.dataset.collectionTab);
    }

    tablist.addEventListener('click', onTabClick);
    panelsRoot.addEventListener('click', onTabClick);

    function onTabKeydown(event) {
      const tab = event.target.closest('[data-collection-tab]');
      if (!tab) return;
      const group = tab.closest('[data-collection-subtabs]')
        ? Array.from(tab.closest('[data-collection-subtabs]').querySelectorAll('[data-collection-tab]'))
        : categoryTabs;
      const index = group.indexOf(tab);
      if (index < 0) return;

      let next = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        next = group[(index + 1) % group.length];
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        next = group[(index - 1 + group.length) % group.length];
      } else if (event.key === 'Home') {
        next = group[0];
      } else if (event.key === 'End') {
        next = group[group.length - 1];
      }

      if (next) {
        event.preventDefault();
        next.focus();
        setActive(next.dataset.collectionTab);
      }
    }

    tablist.addEventListener('keydown', onTabKeydown);
    panelsRoot.addEventListener('keydown', onTabKeydown);

    let initialTab = defaultTab;
    const postedPanel = panelsRoot.querySelector('[data-event-enquiry-success]');
    if (postedPanel) {
      const postedTab = postedPanel.closest('[data-collection-panel]');
      if (postedTab && panelMap.has(postedTab.dataset.collectionPanel)) {
        initialTab = postedTab.dataset.collectionPanel;
      }
    }
    const hashTab = window.location.hash.replace(/^#/, '');
    if (!postedPanel && hashTab && (panelMap.has(hashTab) || PARENTS[hashTab])) {
      initialTab = hashTab;
    } else if (!postedPanel) {
      try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        if (stored && (panelMap.has(stored) || PARENTS[stored])) initialTab = stored;
      } catch (e) {
        /* ignore */
      }
    }

    function scrollToHashTarget(tabId) {
      const target = resolveTarget(tabId, panelMap);
      if (!target) return;
      const parent = parentOf(target);
      const tab = categoryTabs.find((item) => {
        const id = item.dataset.collectionTab;
        return id === tabId || id === target || id === parent;
      });
      const panel = panelMap.get(target);
      if (tab) {
        tab.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
      }
      if (panel) {
        panel.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    }

    setActive(initialTab, { updateHash: false });
    if (hashTab && (panelMap.has(hashTab) || PARENTS[hashTab]) && !postedPanel) {
      requestAnimationFrame(() => scrollToHashTarget(hashTab));
    }

    window.addEventListener('hashchange', () => {
      const nextHash = window.location.hash.replace(/^#/, '');
      if (nextHash && (panelMap.has(nextHash) || PARENTS[nextHash])) {
        setActive(nextHash, { updateHash: false });
        scrollToHashTarget(nextHash);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
